import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  BotRefusalFrame,
  BotToolProgressFrame,
  BotToolStatusFrame,
  Frame,
  FrameKind,
  isBotChunk,
  isBotComplete,
  isCitations,
  isDocumentIngest,
  isError,
  isPolicyDecision,
  isFactsUpdated,
  isRefusal,
  isToolEnd,
  isToolProgress,
  isToolStart,
  isWebSearchResult,
  toolNameOf,
  type Citation,
  type PlanTodo,
  type IterationEndFrame,
  type IterationStartFrame,
  type BudgetWarningFrame,
  type BudgetPausedFrame,
  type ReviewRequiredFrame,
  type RecipientVerdict,
  type AttachmentPreview,
  type PromoteDocumentPreview,
} from "../events";
import type { FrameRegistry } from "../extensions/frameRegistry";
import { readExportFormats, readTablePreview } from "../artifactFields";
import type {
  AssistantPart,
  AssistantTextPart,
  AssistantArtifactPart,
  AssistantMediaPart,
  AssistantSubagentPart,
  WorkEntry,
} from "../types";

/** Watchdog window: if no frame arrives for this long the stream is treated
 *  as dead and the turn is finalized rather than spinning forever. The backend
 *  emits tool heartbeats every ~5s while a tool runs and tokens flow during
 *  generation, so a 90s gap (18 missed heartbeats) means a genuinely dead
 *  connection, not a slow turn. */
const IDLE_TIMEOUT_MS = 90_000;

export enum TurnStatus {
  Idle = "idle",
  Connecting = "connecting",
  Preparing = "preparing",
  Streaming = "streaming",
  Complete = "complete",
  Cancelled = "cancelled",
  Error = "error",
}

export interface ActiveTool {
  name: string;
  call_id?: string;
  /** Most recent progress/heartbeat message — drives the secondary line
   *  under the busy chip ("Still working… (12s)"). */
  message?: string | null;
  /** Set on the first synthetic heartbeat so the UI can render a softer
   *  state (e.g. dimmed text) instead of treating it like real progress. */
  isHeartbeat?: boolean;
  /** Friendly label captured from the tool_start frame (bot_tool_status
   *  payload.label, e.g. "Searching…"). Persisted into the toolCalls record
   *  on tool_end so completed chips can display the human-readable name. */
  label?: string;
}

/** A completed tool call captured during a turn for post-turn display. */
export interface ToolCallRecord {
  name: string;
  label?: string;
  isError?: boolean;
}

export interface PendingConfirmation {
  action: string;
  summary: string;
  params: Record<string, unknown>;
}

/** Progress of a knowledge-base document ingestion (document_ingest). One
 *  entry per document, keyed by document_id (or filename before an id
 *  exists). Drives an inline progress chip so the user sees an uploaded /
 *  tool-ingested file move processing → ready → failed without leaving chat. */
export interface IngestStatus {
  document_id?: number;
  filename?: string;
  scope?: string;
  status: "processing" | "ready" | "failed" | "duplicate" | "duplicate_personal";
  error?: string;
  /** Set on a "duplicate"/"duplicate_personal" status — the id of the
   *  pre-existing document this upload matched. */
  duplicate_of_document_id?: number;
}

/** Outcome of a mutating tool call (bot_action_result). Surfaced as a
 *  confirmation pill so the user gets an explicit "✓ Renamed role" /
 *  "✗ Failed to …" signal — the "did my write land?" answer an ERP
 *  assistant needs, distinct from the generic tool-end chip. */
export interface ActionResult {
  action: string;
  status: "succeeded" | "failed";
  latency_ms?: number | null;
}

/** One agent-loop tool-iteration, tracked from iteration_start through
 *  iteration_end for a step-timeline UI. Reset on a new turn. */
export interface TurnStep {
  iteration: number;
  model: string;
  nTools: number;
  toolNames: string[];
  done?: boolean;
  stopReason?: string | null;
  iterCostUsd?: number;
  cumulativeCostUsd?: number;
  inputTokens?: number;
  outputTokens?: number;
  elapsedS?: number;
}

/** Live per-turn budget snapshot (iteration_end's budget sub-object plus the
 *  running cumulative cost/elapsed, and a soft-warning flag from
 *  budget_warning). Drives a live budget meter. */
export interface TurnBudgetState {
  maxTurnCostUsd: number;
  maxTurnSeconds: number;
  maxToolIterations: number;
  cumulativeCostUsd?: number;
  elapsedS?: number;
  warn?: boolean;
}

/** A gated tool call the turn ENDED on, awaiting the user's approve/reject
 *  (review_required). Drives the approval card; cleared on the next turn.
 *
 *  `error` is set INSTEAD of `args` when the gate could not persist the pending
 *  call. It is kept rather than dropped because the gate is fail-CLOSED: the
 *  tool did not run, there is nothing to approve, and the card must say so
 *  instead of offering a button whose decision can never be read. */
export interface PendingReview {
  call_id: string;
  tool_name: string;
  summary: string | null;
  args: Record<string, unknown> | null;
  error: string | null;
  /** Per-recipient verdicts for a gated `send_email` (spec §3), or ABSENT when
   *  the deployment could not resolve them. Absent is a degraded render, not an
   *  error — see the reducer. */
  recipients?: RecipientVerdict[];
  /** The files a gated `send_email` will attach, named by the server (T15c),
   *  or ABSENT when it could not describe them — in which case the card falls
   *  back to the count it always showed. */
  attachments?: AttachmentPreview[];
  /** The file a gated `promote_to_knowledge` would add, named by the server
   *  through the promote's own write gate, or ABSENT when it was not
   *  described (the card then shows the bare id). */
  document?: PromoteDocumentPreview;
}

/** Set when the turn paused because a budget dimension was exceeded
 *  (budget_paused). Drives a resume/stop prompt; cleared on the next turn. */
export interface TurnBudgetPause {
  dimension: "cost" | "time" | "iterations";
  reason: string;
}

export interface TurnMetrics {
  ttft_ms?: number;
  total_latency_ms?: number;
  cost_usd?: number;
  input_tokens?: number;
  cache_read_input_tokens?: number;
  model?: string;
}

export interface TurnState {
  status: TurnStatus;
  assistantParts: AssistantPart[];
  thinkingText: string;
  activeTool: ActiveTool | null;
  /** Persistent record of all tool calls that completed this turn (excluding
   *  spawn_subagent which gets its own SubagentCard). Promoted into the
   *  history Message on turn completion so chips stay visible. */
  toolCalls: ToolCallRecord[];
  citations: Citation[];
  error: string | null;
  /** True when the backend signalled stop_reason="refusal" (typed
   *  bot_refusal frame) — the UI renders this as a muted refusal card,
   *  distinct from a generic error. */
  refused: boolean;
  refusalText: string | null;
  /** Monotonic counter incremented every time the server emits
   *  facts_updated. The Memory-updated pill keys off this so it can
   *  fade out on a timer and re-appear if facts change twice in a row. */
  factsUpdatedAt: number;
  /** Accumulates spawn_subagent args between tool_start and tool_end so we
   *  can create the AssistantSubagentPart once the job_id is known. */
  pendingSpawnArgs: Record<
    string,
    { prompt: string; tools: string[]; name: string }
  >;
  /** Set when the model emits a <confirm> block (bot_confirmation). The UI
   *  renders an approve/cancel card; responding reuses the chat send path.
   *  Cleared on the next Connected (new turn). */
  pendingConfirmation: PendingConfirmation | null;
  /** Final turn cost/latency/token usage (turn_metrics frame). Rendered as
   *  a quiet footer under the completed assistant turn. */
  metrics: TurnMetrics | null;
  /** Narration lines emitted by the model via bot_narration_promote. Shown in
   *  the expanded WorkingPill to give context for what the model was doing.
   *  Persisted onto the completed Message for post-turn display. */
  workingNotes: string[];
  /** Chronological work timeline for this turn (reasoning + narration + tool
   *  steps in arrival order). Drives WorkTimeline; promoted/persisted so it
   *  survives turn-complete and reload. */
  workLog: WorkEntry[];
  /** Char offset into the trailing text part marking where real answer text
   *  ends and not-yet-promoted narration begins. On bot_narration_promote we
   *  remove everything past this boundary (the narration span). Reset on a new
   *  turn and re-anchored after each strip. */
  narrationBoundary: number;
  /** Outcomes of mutating tool calls (bot_action_result), in arrival order.
   *  Rendered as confirmation pills below the turn so the user can see which
   *  writes landed. Reset on a new turn. */
  actionResults: ActionResult[];
  /** KB document ingestions observed this turn (document_ingest), upserted by
   *  document_id/filename so a file's chip advances processing → ready/failed
   *  in place. Reset on a new turn. */
  ingests: IngestStatus[];
  /** Live plan (write_todos snapshot, plan_updated). Replaced each emit. */
  plan: PlanTodo[];
  /** Set when blocking plan mode proposes a plan and the turn ends awaiting
   *  the user's approve/edit/reject decision. Cleared on the next turn. */
  pendingPlan: { plan_id: string; todos: PlanTodo[] } | null;
  /** Set when the approval gate held a tool call and the turn ended awaiting
   *  the user's approve/reject (review_required). Cleared on the next turn. */
  pendingReview: PendingReview | null;
  /** Agent-loop tool-iteration timeline (iteration_start/iteration_end).
   *  Reset on a new turn. */
  steps: TurnStep[];
  /** Live turn budget snapshot (iteration_end + budget_warning). Undefined
   *  until the first iteration_end/budget_warning arrives. */
  budget?: TurnBudgetState;
  /** Set when the turn is paused awaiting a budget_decision (budget_paused).
   *  Cleared on the next turn. */
  budgetPause?: TurnBudgetPause;
  /** Wall-clock ms when the first thinking chunk arrived. Used to measure
   *  reasoning duration. Undefined until reasoning starts. */
  thinkingStartedAt?: number;
  /** Measured reasoning duration (ms), fixed when the answer starts (or the
   *  turn completes). Drives ThinkingBlock's "Thought for Xs". */
  thinkingMs?: number;
  /** Set on tool_end so the next answer chunk begins a FRESH text part instead
   *  of appending to the preceding (narration) paragraph. Keeps the post-tool
   *  final answer on its own line rather than running together with the
   *  inter-tool narration ("…same time.According to…"). Cleared once consumed. */
  answerBlockBreakPending: boolean;
}

export const initialTurnState: TurnState = {
  status: TurnStatus.Idle,
  assistantParts: [],
  thinkingText: "",
  activeTool: null,
  toolCalls: [],
  citations: [],
  error: null,
  refused: false,
  refusalText: null,
  factsUpdatedAt: 0,
  pendingSpawnArgs: {},
  pendingConfirmation: null,
  metrics: null,
  workingNotes: [],
  workLog: [],
  narrationBoundary: 0,
  actionResults: [],
  ingests: [],
  plan: [],
  pendingPlan: null,
  pendingReview: null,
  steps: [],
  budget: undefined,
  budgetPause: undefined,
  thinkingStartedAt: undefined,
  thinkingMs: undefined,
  answerBlockBreakPending: false,
};

function appendText(parts: AssistantPart[], text: string): AssistantPart[] {
  const last = parts[parts.length - 1];
  if (last?.type === "text") {
    return [...parts.slice(0, -1), { ...last, text: last.text + text }];
  }
  return [...parts, { type: "text", text }];
}

/** Remove the narration span from the trailing text part on promote, anchored
 *  to `boundary` (the char length already committed as real answer text since
 *  this text part began). Backend streams between-tool narration as
 *  bot_message_chunk first, then re-labels it via bot_narration_promote — so
 *  the prose would otherwise render BOTH as a bubble and in the pill. The
 *  promote reliably arrives before the next iteration's answer text, so at
 *  promote time everything from `boundary` to the end of the trailing text
 *  part IS the narration. We slice that span off (not an endswith match, which
 *  breaks when the parser-filtered bubble text differs from the raw narration
 *  text). An emptied text part is dropped so it renders nothing. */
function stripNarrationSpan(
  parts: AssistantPart[],
  boundary: number,
  narration?: string,
): AssistantPart[] {
  const last = parts[parts.length - 1];
  if (last?.type !== "text") return parts;
  // Prefer removing the EXACT promoted narration from the end of the trailing
  // text. This is robust to the promote arriving AFTER tool_end: tool_end
  // commits `boundary` to the current length (to protect real answer text), so
  // if the narration hasn't been promoted yet the boundary slice below is a
  // no-op and the narration would leak into the answer bubble. Matching the
  // promoted text directly strips it regardless of where the boundary sits.
  if (narration && last.text.endsWith(narration)) {
    const kept = last.text.slice(0, last.text.length - narration.length);
    if (kept.length === 0) return parts.slice(0, -1);
    return [...parts.slice(0, -1), { ...last, text: kept }];
  }
  // Fallback: slice everything past the committed boundary (covers cases where
  // the stored text differs from the raw promoted text, e.g. chunk coalescing).
  if (last.text.length <= boundary) return parts;
  const kept = last.text.slice(0, boundary);
  if (kept.length === 0) return parts.slice(0, -1);
  return [...parts.slice(0, -1), { ...last, text: kept }];
}

/** Length of the trailing text part (0 if the last part isn't text), used to
 *  reset the narration boundary after a span is removed or text is committed. */
function trailingTextLen(parts: AssistantPart[]): number {
  const last = parts[parts.length - 1];
  return last?.type === "text" ? last.text.length : 0;
}

function findArtifactPart(
  parts: AssistantPart[],
  id: string,
): AssistantArtifactPart | undefined {
  return parts.find(
    (p): p is AssistantArtifactPart =>
      p.type === "artifact" && p.artifact_id === id,
  );
}

function patchArtifact(
  parts: AssistantPart[],
  id: string,
  patch: Partial<AssistantArtifactPart>,
): AssistantPart[] {
  const idx = parts.findIndex(
    (p) => p.type === "artifact" && p.artifact_id === id,
  );
  if (idx === -1) return parts;
  const next = [...parts];
  next[idx] = { ...(next[idx] as AssistantArtifactPart), ...patch };
  return next;
}

/** Append a thinking/reasoning delta: extend the trailing reasoning entry, or
 *  start a new one if the last entry isn't reasoning (i.e. a tool/narration
 *  interrupted). This yields reasoning → tool → reasoning segmentation. */
function workPushReasoning(log: WorkEntry[], text: string): WorkEntry[] {
  if (!text) return log;
  const last = log[log.length - 1];
  if (last?.kind === "reasoning") {
    return [...log.slice(0, -1), { kind: "reasoning", text: last.text + text }];
  }
  return [...log, { kind: "reasoning", text }];
}

function workPushNarration(log: WorkEntry[], text: string): WorkEntry[] {
  if (!text) return log;
  return [...log, { kind: "narration", text }];
}

function workOpenTool(
  log: WorkEntry[],
  name: string,
  label: string,
): WorkEntry[] {
  return [
    ...log,
    { kind: "tool", name, label: label || name, status: "running" },
  ];
}

/** Attach citations to the most recent still-running tool entry (append + dedupe
 *  by url). No-op if there's no open tool entry. */
function workAttachResults(log: WorkEntry[], cites: Citation[]): WorkEntry[] {
  if (!cites.length) return log;
  for (let i = log.length - 1; i >= 0; i--) {
    const e = log[i];
    if (e.kind === "tool" && e.status === "running") {
      const seen = new Set((e.results ?? []).map((c) => c.url).filter(Boolean));
      const fresh = cites.filter((c) => !c.url || !seen.has(c.url));
      const next = [...log];
      next[i] = { ...e, results: [...(e.results ?? []), ...fresh] };
      return next;
    }
  }
  return log;
}

function workCloseTool(log: WorkEntry[], isError: boolean): WorkEntry[] {
  for (let i = log.length - 1; i >= 0; i--) {
    const e = log[i];
    if (e.kind === "tool" && e.status === "running") {
      const next = [...log];
      next[i] = { ...e, status: isError ? "error" : "done" };
      return next;
    }
  }
  return log;
}

/**
 * Pure reducer: (state, frame, registry) -> state.
 * Registry is currently a render-time concern (MessageList consults
 * registry.rendererFor()). It's threaded through the reducer signature
 * so future extensions can also influence state.
 */
export function turnReducer(
  state: TurnState,
  frame: Frame,
  _registry: FrameRegistry | null,
): TurnState {
  if (frame.kind === FrameKind.Connected) {
    // Connected starts a new turn — reset transient state so a prior
    // turn's assistantParts/citations/error don't leak into this one.
    return { ...initialTurnState, status: TurnStatus.Connecting };
  }
  if (frame.kind === FrameKind.PreparingResponse) {
    return { ...state, status: TurnStatus.Preparing };
  }
  if (isBotChunk(frame)) {
    // First answer chunk ends the reasoning phase — fix the measured duration.
    const thinkingMs =
      state.thinkingMs ??
      (state.thinkingStartedAt != null
        ? Date.now() - state.thinkingStartedAt
        : undefined);
    // After a tool, the answer starts a fresh text part (its own paragraph)
    // rather than appending onto the trailing narration text.
    const assistantParts =
      state.answerBlockBreakPending && frame.text
        ? [...state.assistantParts, { type: "text" as const, text: frame.text }]
        : appendText(state.assistantParts, frame.text);
    return {
      ...state,
      status: TurnStatus.Streaming,
      assistantParts,
      answerBlockBreakPending: state.answerBlockBreakPending && !frame.text,
      thinkingMs,
    };
  }
  if (isBotComplete(frame)) {
    // A budget_paused turn is HELD for the user's Continue/Stop decision — it
    // is not final. The backend still emits the terminal done frame right
    // after budget_paused (to close the SSE turn), but flipping to Complete
    // here would (a) promote the turn to history prematurely and (b) hide the
    // live bubble, making a just-completed artifact vanish ("stuck on
    // streaming…"). Keep the paused turn out of Complete; resume/stop drives
    // the next transition.
    if (state.budgetPause) return state;
    // The loop always emits a terminal done frame after a typed error
    // (context_too_large, repeated_tool_call, …). That done becomes
    // bot_message_complete and must not clobber Error — otherwise the
    // bubble looks empty (tools + metrics, no message, no error).
    if (state.status === TurnStatus.Error) return state;
    const thinkingMs =
      state.thinkingMs ??
      (state.thinkingStartedAt != null
        ? Date.now() - state.thinkingStartedAt
        : undefined);
    return { ...state, status: TurnStatus.Complete, thinkingMs };
  }
  if (frame.kind === FrameKind.BotThinkingChunk) {
    return {
      ...state,
      thinkingText: state.thinkingText + ((frame as any).text ?? ""),
      thinkingStartedAt: state.thinkingStartedAt ?? Date.now(),
      workLog: workPushReasoning(state.workLog, (frame as any).text ?? ""),
    };
  }
  if (frame.kind === FrameKind.ThinkingComplete) {
    return {
      ...state,
      thinkingMs: state.thinkingMs ?? (frame as any).thinking_ms ?? undefined,
    };
  }
  if (frame.kind === FrameKind.BotNarrationPromote) {
    const text = (frame as any).text as string | undefined;
    if (!text) return state;
    // The narration prose was already streamed as bot_message_chunk text and
    // lives in assistantParts (it would otherwise render as a duplicate
    // message bubble). Remove the span from the boundary to the end of the
    // trailing text part — that span is the just-finished narration — and
    // re-anchor the boundary so the next iteration's real answer text accrues
    // beyond it and survives. Record the note for the WorkingPill regardless.
    const stripped = stripNarrationSpan(
      state.assistantParts,
      state.narrationBoundary,
      text,
    );
    return {
      ...state,
      assistantParts: stripped,
      // Re-anchor to the post-strip length so any subsequent answer text in
      // this same part is kept, and so a later promote (without an intervening
      // tool_end) doesn't re-strip already-kept text.
      narrationBoundary: trailingTextLen(stripped),
      workingNotes: [...state.workingNotes, text],
      workLog: workPushNarration(state.workLog, text),
    };
  }
  if (frame.kind === FrameKind.BotConfirmation) {
    return {
      ...state,
      pendingConfirmation: {
        action: (frame as any).action ?? "",
        summary: (frame as any).summary ?? "",
        params: (frame as any).params ?? {},
      },
    };
  }
  if (frame.kind === FrameKind.PlanUpdated) {
    return { ...state, plan: ((frame as any).todos ?? []) as PlanTodo[] };
  }
  if (frame.kind === FrameKind.PlanProposed) {
    const todos = ((frame as any).todos ?? []) as PlanTodo[];
    return {
      ...state,
      // Keep the live plan in sync with what's being proposed.
      plan: todos,
      pendingPlan: { plan_id: (frame as any).plan_id ?? "", todos },
    };
  }
  if (frame.kind === FrameKind.ReviewRequired) {
    const f = frame as ReviewRequiredFrame;
    return {
      ...state,
      pendingReview: {
        call_id: f.call_id ?? "",
        tool_name: f.tool_name ?? "",
        summary: f.summary ?? null,
        // Normalised to null rather than left undefined so the card can branch
        // on presence without distinguishing "absent" from "explicitly null" —
        // the backend sends null for whichever of these the gate did not fill.
        args: f.args ?? null,
        error: f.error ?? null,
        // OMITTED, never defaulted (matching the server, which omits the key
        // rather than sending null): absent means the addresses were never
        // checked, and `[]` would instead claim there are no recipients.
        ...(Array.isArray(f.recipients) ? { recipients: f.recipients } : {}),
        // Same rule for the attachment names: absent means the gate could not
        // describe them, and `[]` would instead claim this send attaches
        // nothing at all.
        ...(Array.isArray(f.attachments) ? { attachments: f.attachments } : {}),
        // Same rule again for the file a promote would add.
        ...(f.document && typeof f.document === "object" ? { document: f.document } : {}),
      },
    };
  }
  if (isDocumentIngest(frame)) {
    const incoming: IngestStatus = {
      document_id: (frame as any).document_id,
      filename: (frame as any).filename,
      scope: (frame as any).scope,
      status: ((frame as any).status as IngestStatus["status"]) ?? "processing",
      error: (frame as any).error,
      duplicate_of_document_id: (frame as any).duplicate_of_document_id,
    };
    // Upsert: a document advances processing → ready/failed across frames, so
    // match an existing entry (by document_id, else filename) and replace it
    // in place rather than stacking duplicate chips.
    const matches = (e: IngestStatus) =>
      incoming.document_id != null
        ? e.document_id === incoming.document_id
        : incoming.filename != null && e.filename === incoming.filename;
    const idx = state.ingests.findIndex(matches);
    if (idx === -1) {
      return { ...state, ingests: [...state.ingests, incoming] };
    }
    const next = [...state.ingests];
    next[idx] = { ...next[idx], ...incoming };
    return { ...state, ingests: next };
  }
  if (frame.kind === FrameKind.BotActionResult) {
    return {
      ...state,
      actionResults: [
        ...state.actionResults,
        {
          action: (frame as any).action ?? "",
          status: (frame as any).status === "failed" ? "failed" : "succeeded",
          latency_ms: (frame as any).latency_ms ?? null,
        },
      ],
    };
  }
  if (frame.kind === FrameKind.TurnMetrics) {
    return {
      ...state,
      metrics: {
        ttft_ms: (frame as any).ttft_ms,
        total_latency_ms: (frame as any).total_latency_ms,
        cost_usd: (frame as any).cost_usd,
        input_tokens: (frame as any).input_tokens,
        cache_read_input_tokens: (frame as any).cache_read_input_tokens,
        model: (frame as any).model,
      },
    };
  }
  if (frame.kind === FrameKind.IterationStart) {
    const f = frame as IterationStartFrame;
    const step: TurnStep = {
      iteration: f.iteration,
      model: f.model,
      nTools: f.n_tools,
      toolNames: f.tool_names ?? [],
    };
    return { ...state, steps: [...state.steps, step] };
  }
  if (frame.kind === FrameKind.IterationEnd) {
    const f = frame as IterationEndFrame;
    const idx = state.steps.findIndex((s) => s.iteration === f.iteration);
    let steps = state.steps;
    if (idx !== -1) {
      const next = [...state.steps];
      next[idx] = {
        ...next[idx],
        done: true,
        stopReason: f.stop_reason ?? null,
        iterCostUsd: f.iter_cost_usd,
        cumulativeCostUsd: f.cumulative_cost_usd,
        inputTokens: f.input_tokens,
        outputTokens: f.output_tokens,
        elapsedS: f.elapsed_s,
      };
      steps = next;
    }
    const budget: TurnBudgetState | undefined = f.budget
      ? {
          maxTurnCostUsd: f.budget.max_turn_cost_usd,
          maxTurnSeconds: f.budget.max_turn_seconds,
          maxToolIterations: f.budget.max_tool_iterations,
          cumulativeCostUsd: f.cumulative_cost_usd,
          elapsedS: f.elapsed_s,
          warn: state.budget?.warn,
        }
      : state.budget
        ? {
            ...state.budget,
            cumulativeCostUsd: f.cumulative_cost_usd,
            elapsedS: f.elapsed_s,
          }
        : undefined;
    return { ...state, steps, budget };
  }
  if (frame.kind === FrameKind.BudgetWarning) {
    const f = frame as BudgetWarningFrame;
    return {
      ...state,
      budget: state.budget
        ? { ...state.budget, warn: true }
        : {
            maxTurnCostUsd: 0,
            maxTurnSeconds: 0,
            maxToolIterations: 0,
            cumulativeCostUsd: f.cumulative_cost_usd,
            elapsedS: f.elapsed_s,
            warn: true,
          },
    };
  }
  if (frame.kind === FrameKind.BudgetPaused) {
    const f = frame as BudgetPausedFrame;
    return {
      ...state,
      budgetPause: {
        dimension: f.dimension,
        reason: f.reason,
      },
    };
  }
  if (isToolStart(frame)) {
    const tool = toolNameOf(frame) ?? "";
    const call_id =
      frame.kind === FrameKind.BotToolStatus
        ? undefined
        : (frame as any).tool_call_id;
    // Capture the friendly label from the bot_tool_status payload (e.g. "Searching…")
    const label = (frame as any).label as string | undefined;
    // Capture spawn_subagent args so we can build the subagent part when
    // tool_end arrives with the job_id.
    if (tool === "spawn_subagent") {
      const key = (frame as any).tool_call_id ?? "current";
      const args = (frame as any).args ?? {};
      return {
        ...state,
        activeTool: {
          name: tool,
          call_id,
          message: null,
          isHeartbeat: false,
          label,
        },
        pendingSpawnArgs: {
          ...state.pendingSpawnArgs,
          [key]: {
            prompt: args.prompt ?? "",
            tools: args.tools ?? [],
            name:
              args.name ??
              (args.prompt ? String(args.prompt).slice(0, 60) : "Subagent"),
          },
        },
        workLog: workOpenTool(state.workLog, tool, (frame as any).label ?? ""),
      };
    }
    return {
      ...state,
      activeTool: {
        name: tool,
        call_id,
        message: null,
        isHeartbeat: false,
        label,
      },
      workLog: workOpenTool(state.workLog, tool, (frame as any).label ?? ""),
    };
  }
  if (isToolEnd(frame)) {
    // Materialise a running subagent part when spawn_subagent completes.
    const toolName = toolNameOf(frame) ?? "";
    if (toolName === "spawn_subagent") {
      const key = (frame as any).tool_call_id ?? "current";
      const pending = state.pendingSpawnArgs?.[key];
      const jobId = (frame as any).result?.job_id;
      if (jobId && pending) {
        const { [key]: _drop, ...remainingPending } = state.pendingSpawnArgs;
        // A racing subagent_event may have already materialised this part.
        // If so, just clear pending/activeTool — don't append a duplicate.
        const exists = state.assistantParts.some(
          (p) =>
            p.type === "subagent" &&
            (p as AssistantSubagentPart).job_id === jobId,
        );
        if (exists) {
          return {
            ...state,
            activeTool: null,
            pendingSpawnArgs: remainingPending,
          };
        }
        return {
          ...state,
          activeTool: null,
          pendingSpawnArgs: remainingPending,
          // spawn_subagent gets a SubagentCard — skip adding a toolCall record
          // to avoid double-showing.
          assistantParts: [
            ...state.assistantParts,
            {
              type: "subagent",
              job_id: jobId,
              name: pending.name,
              tools: pending.tools,
              status: "running",
              current_event_kind: null,
              events: [],
              result: null,
              error: null,
            } satisfies AssistantSubagentPart,
          ],
        };
      }
      // spawn_subagent without a job_id — clear activeTool but still no record
      return { ...state, activeTool: null };
    }
    // For all non-subagent tools: append a persistent toolCall record so the
    // completed chip stays visible after the turn is promoted to history.
    const endLabel = (frame as any).label as string | undefined;
    const record: ToolCallRecord = {
      name: toolName,
      label: state.activeTool?.label ?? endLabel,
      isError: (frame as any).is_error ?? false,
    };
    // NOTE: KB citations are NOT derived here. search_knowledge results are
    // forwarded by the backend as a dedicated `web_search_result` frame
    // (type:'kb'), handled by the isWebSearchResult branch below — the live
    // tool_end (bot_tool_status phase=end) frame carries no result anyway.
    return {
      ...state,
      activeTool: null,
      toolCalls: [...state.toolCalls, record],
      // Commit any answer text streamed so far: a new iteration (more
      // narration) may follow, and its promote must only strip text accrued
      // after this point — not the kept text before it.
      narrationBoundary: trailingTextLen(state.assistantParts),
      // Start the post-tool answer in a fresh block only for CLIENT tools —
      // their result ends the iteration and the answer legitimately restarts.
      // A SERVER tool (web_search) runs mid-response: the surrounding text is
      // one continuous answer and the model can emit the tool mid-word, so
      // forcing a break here splits a word ("…insu" | "rance…"). Don't break
      // for it — let the text flow into the same block. (Future server tools
      // e.g. OpenAI/Gemini web search should be added to this guard.)
      answerBlockBreakPending:
        toolName === "web_search" ? state.answerBlockBreakPending : true,
      workLog: workCloseTool(state.workLog, (frame as any).is_error ?? false),
    };
  }
  if (isToolProgress(frame)) {
    // Real or heartbeat. Either way, refresh the busy chip's secondary
    // line so the user sees the tool is still alive.
    if (!state.activeTool) return state;
    const f = frame as BotToolProgressFrame;
    const message =
      typeof (frame as any).message === "string"
        ? (frame as any).message
        : typeof (frame as any).text === "string"
          ? (frame as any).text
          : (state.activeTool.message ?? null);
    return {
      ...state,
      activeTool: {
        ...state.activeTool,
        message,
        isHeartbeat: Boolean(f.heartbeat),
      },
    };
  }
  if (isCitations(frame)) {
    return { ...state, citations: frame.citations };
  }
  if (isWebSearchResult(frame)) {
    const incoming = (frame.citations ?? []).map((c) => ({
      ...c,
      type: c.type ?? ("web" as const),
    }));
    // append + dedupe by url (web citations have urls); keep first occurrence
    const seen = new Set(state.citations.map((c) => c.url).filter(Boolean));
    const fresh = incoming.filter((c) => !c.url || !seen.has(c.url));
    return {
      ...state,
      citations: [...state.citations, ...fresh],
      workLog: workAttachResults(state.workLog, incoming),
    };
  }
  if (isRefusal(frame)) {
    // bot_refusal carries `text` (possibly empty); legacy `refusal` carries
    // `reason`. Either becomes refusalText.
    const txt =
      (frame as BotRefusalFrame).text ??
      (frame as any).reason ??
      "I can't help with that.";
    return {
      ...state,
      status: TurnStatus.Error,
      refused: true,
      refusalText: txt || null,
    };
  }
  if (isFactsUpdated(frame)) {
    // Bump the counter; the UI watches this and shows a transient pill.
    return { ...state, factsUpdatedAt: state.factsUpdatedAt + 1 };
  }
  if (isError(frame) && isPolicyDecision(frame)) {
    // A policy refusal is a decision, not a fault. Route it to the refusal
    // card and leave `error` unset — MessageList renders the red banner only
    // when there is an error AND no refusal, so setting both double-renders.
    return {
      ...state,
      status: TurnStatus.Error,
      refused: true,
      refusalText:
        frame.message || "This message was blocked by an organisation policy.",
    };
  }
  if (isError(frame)) {
    return { ...state, status: TurnStatus.Error, error: frame.message };
  }
  if (frame.kind === FrameKind.StreamClosed) {
    // The SSE stream ended without a terminal frame (network drop, backend
    // crash, proxy timeout). Only finalize a turn that is still mid-stream —
    // a turn already Complete/Error/Cancelled, or one that never started
    // (Idle), is left untouched. With partial content, keep what streamed and
    // mark Complete so it promotes to history; with nothing, surface an error
    // rather than leaving a blank, perpetually-spinning bubble.
    // A budget-paused turn is intentionally held (not Complete) with the SSE
    // stream closed — the user must Continue/Stop. Don't let stream-close
    // finalize it to Complete, or the held turn promotes prematurely and its
    // live bubble (with the completed artifact) disappears.
    if (state.budgetPause) return state;
    const midStream =
      state.status === TurnStatus.Connecting ||
      state.status === TurnStatus.Preparing ||
      state.status === TurnStatus.Streaming;
    if (!midStream) return state;
    if (state.assistantParts.length > 0) {
      return { ...state, status: TurnStatus.Complete };
    }
    return {
      ...state,
      status: TurnStatus.Error,
      error: "The connection was interrupted before the response finished.",
    };
  }
  if (frame.kind === FrameKind.ArtifactStart) {
    const newPart: AssistantArtifactPart = {
      type: "artifact",
      artifact_id: (frame as any).artifact_id,
      artifact_kind: (frame as any).artifact_kind,
      title: (frame as any).title ?? null,
      mime: (frame as any).mime ?? null,
      language: (frame as any).language ?? null,
      text: "",
      status: "streaming",
      review_required: (frame as any).review_required ?? undefined,
      display: (frame as any).display ?? undefined,
      ...(typeof (frame as any).size === "number" ? { size: (frame as any).size } : {}),
      ...readExportFormats((frame as any).export_formats),
      ...readTablePreview((frame as any).preview),
    };
    return { ...state, assistantParts: [...state.assistantParts, newPart] };
  }
  if (frame.kind === FrameKind.ArtifactChunk) {
    const cur = findArtifactPart(
      state.assistantParts,
      (frame as any).artifact_id,
    );
    return {
      ...state,
      assistantParts: patchArtifact(
        state.assistantParts,
        (frame as any).artifact_id,
        {
          text: (cur?.text ?? "") + (frame as any).text,
        },
      ),
    };
  }
  if (frame.kind === FrameKind.ArtifactEnd) {
    return {
      ...state,
      assistantParts: patchArtifact(
        state.assistantParts,
        (frame as any).artifact_id,
        {
          status: (frame as any).status === "succeeded" ? "complete" : "failed",
          error: (frame as any).error ?? undefined,
        },
      ),
    };
  }
  if (frame.kind === FrameKind.BotMedia) {
    const newPart: AssistantMediaPart = {
      type: "media",
      artifact_id: frame.artifact_id,
      media_kind: frame.media_kind,
      mime: frame.mime,
      url: frame.url,
      alt: frame.alt,
      width: frame.width,
      height: frame.height,
      duration_ms: frame.duration_ms,
      prompt: frame.prompt,
      status: "ready",
    };
    return { ...state, assistantParts: [...state.assistantParts, newPart] };
  }
  if (frame.kind === FrameKind.SubagentEvent) {
    let idx = state.assistantParts.findIndex(
      (p) =>
        p.type === "subagent" &&
        (p as AssistantSubagentPart).job_id === (frame as any).job_id,
    );
    let baseParts = state.assistantParts;
    if (idx === -1) {
      // No part yet. The backend filters subagent events to this session, so
      // an unknown job_id here is a same-session subagent whose spawn_subagent
      // tool_end hasn't landed yet (worker emits events the instant it claims
      // the job). Materialise a running part so the SubagentCard appears; a
      // later tool_end finds this part and won't duplicate it.
      const data = (frame as any).data ?? {};
      const created: AssistantSubagentPart = {
        type: "subagent",
        job_id: (frame as any).job_id,
        name: data.name ?? "Subagent",
        tools: data.tools ?? [],
        status: "running",
        current_event_kind: null,
        events: [],
        result: null,
        error: null,
      };
      baseParts = [...state.assistantParts, created];
      idx = baseParts.length - 1;
    }

    const part = baseParts[idx] as AssistantSubagentPart;
    const seq: number = (frame as any).seq;
    if (part.events.some((e) => e.seq === seq)) return state; // dedupe

    const newEvents = [
      ...part.events,
      {
        seq,
        kind: (frame as any).event_kind,
        data: (frame as any).data,
        ts: (frame as any).ts,
      },
    ];

    let status = part.status;
    let result = part.result;
    let error = part.error;
    const eventKind = (frame as any).event_kind;
    if (eventKind === "done") {
      status = "succeeded";
      result = (frame as any).data?.result ?? null;
    } else if (eventKind === "error") {
      const reason = (frame as any).data?.reason;
      if (reason === "cancelled_by_user") {
        status = "cancelled";
      } else {
        status = "failed";
        error = reason ?? "unknown";
      }
    }

    const updated: AssistantSubagentPart = {
      ...part,
      events: newEvents,
      current_event_kind: eventKind,
      status,
      result,
      error,
    };
    const newParts = [...baseParts];
    newParts[idx] = updated;
    return { ...state, assistantParts: newParts };
  }
  if (frame.kind === FrameKind.Closed) {
    // The push-cancel work surfaces `payload.reason = "cancelled"` on
    // mid-turn cancels; treat that as Cancelled so the UI can render a
    // distinct affordance ("Stopped") vs. an organic Complete.
    const reason = (frame as any).reason;
    if (reason === "cancelled") {
      return { ...state, status: TurnStatus.Cancelled };
    }
    // "budget_stop": the user chose Stop on a budget-paused turn. Clear the
    // pause and finalize so the (completed-so-far) turn promotes to history.
    // This is the one place a paused turn is allowed to reach Complete —
    // isBotComplete/StreamClosed deliberately hold it (see those handlers).
    if (reason === "budget_stop") {
      return { ...state, budgetPause: undefined, status: TurnStatus.Complete };
    }
    return state.status === TurnStatus.Streaming
      ? { ...state, status: TurnStatus.Complete }
      : state;
  }
  return state;
}

export interface UseStreamingTurnArgs {
  stream: AsyncGenerator<Frame, void, void> | null;
  registry: FrameRegistry | null;
  onEvent?: (frame: Frame) => void;
}

type ReducerAction =
  { frame: Frame; registry: FrameRegistry | null } | { reset: true };
function turnReducerForUseReducer(
  state: TurnState,
  action: ReducerAction,
): TurnState {
  // Hard reset to the initial turn (session switch / new chat) — blows away
  // any completed turn's status/parts/metrics so they don't bleed into the
  // next session.
  if ("reset" in action) return initialTurnState;
  return turnReducer(state, action.frame, action.registry);
}

/** An AbortController abort surfacing from fetch or a body read. */
function isAbortError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { name?: unknown }).name === "AbortError"
  );
}

export function useStreamingTurn({
  stream,
  registry,
  onEvent,
}: UseStreamingTurnArgs) {
  const [state, dispatch] = useReducer(
    turnReducerForUseReducer,
    initialTurnState,
  );
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const registryRef = useRef(registry);
  registryRef.current = registry;

  /** Out-of-band bump: call when a `facts_updated` notification arrives on the
   *  events bus (after the SSE turn stream is already closed). Dispatches the
   *  same synthetic frame the in-stream path uses so the Memory-updated pill
   *  appears regardless of the delivery channel. */
  const bumpFactsUpdatedAt = useCallback(() => {
    dispatch({
      frame: { kind: FrameKind.FactsUpdated } as Frame,
      registry: registryRef.current,
    });
  }, []);

  /** Optimistically start a turn on the client BEFORE the backend's `connected`
   *  frame arrives. Flips status to Connecting immediately — resetting any prior
   *  turn's transient state, exactly as the real `connected` frame does — so the
   *  Bolt shows its thinking pose the instant the user hits send, instead of
   *  waiting a network round-trip for the first SSE frame. The real `connected`
   *  frame is a harmless re-reset when it lands. */
  const beginTurn = useCallback(() => {
    dispatch({
      frame: { kind: FrameKind.Connected } as Frame,
      registry: registryRef.current,
    });
  }, []);

  /** Finalize a budget-paused turn the user chose to Stop. The paused turn is
   *  intentionally held out of Complete (see the isBotComplete/StreamClosed
   *  handlers), so Stop must explicitly close it — clearing the pause and
   *  flipping to Complete so the completed-so-far content promotes to history. */
  const finalizeBudgetStop = useCallback(() => {
    dispatch({
      frame: {
        kind: FrameKind.Closed,
        reason: "budget_stop",
      } as unknown as Frame,
      registry: registryRef.current,
    });
  }, []);

  /** Mark the live turn Cancelled, as the server's cancelled frame does. The
   *  client aborts its stream on Stop (ELASV1-3514), so that frame may never
   *  arrive; without this the abort would read as a dropped connection. */
  const markCancelled = useCallback(() => {
    dispatch({
      frame: { kind: FrameKind.Closed, reason: "cancelled" } as unknown as Frame,
      registry: registryRef.current,
    });
  }, []);

  /** Reset the live turn back to Idle/empty. Called when the active session
   *  changes (new chat or a sidebar switch) so a prior turn's completed
   *  status/parts/metrics don't linger into the next session — which would
   *  suppress the new-chat welcome (useIsEmptyChat) and paint a stale metrics
   *  footer on the empty canvas. */
  const reset = useCallback(() => {
    dispatch({ reset: true });
  }, []);

  useEffect(() => {
    if (!stream) return;
    let cancelled = false;

    // Coalesce reducer dispatches to one flush per animation frame. A fast
    // stream can deliver many tokens between paints; dispatching each one
    // separately forces a render per token (the `await` boundary defeats
    // React's auto-batching), which is the main streaming-jank source. We
    // buffer frames and apply them in a single batched pass per rAF tick, so
    // renders are capped at ~display-refresh rate regardless of token rate.
    // `onEvent` still fires immediately per frame, in order, so side effects
    // (subagent-event injection, etc.) keep their original timing.
    let buffer: Frame[] = [];
    let rafId: number | null = null;

    const flush = () => {
      rafId = null;
      if (cancelled || buffer.length === 0) return;
      const frames = buffer;
      buffer = [];
      // All dispatches in this synchronous callback batch into one render.
      for (const frame of frames) {
        dispatch({ frame, registry: registryRef.current });
      }
    };

    const schedule = () => {
      if (rafId != null) return;
      rafId =
        typeof requestAnimationFrame === "function"
          ? requestAnimationFrame(flush)
          : (setTimeout(flush, 16) as unknown as number);
    };

    // Synthetic terminal frame for a stream that ends (or stalls) without a
    // real terminal frame. The reducer no-ops it unless the turn is still
    // mid-stream, so dispatching it on every close/timeout is safe.
    const finalizeOnClose = () => {
      if (cancelled) return;
      dispatch({
        frame: { kind: FrameKind.StreamClosed },
        registry: registryRef.current,
      });
    };

    // Watchdog: if no frame arrives for IDLE_TIMEOUT_MS the stream is stalled
    // (proxy holding a dead connection open, backend hung mid-turn). Finalize
    // rather than spin forever. Re-armed on every frame.
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    const armWatchdog = () => {
      if (idleTimer != null) clearTimeout(idleTimer);
      idleTimer = setTimeout(finalizeOnClose, IDLE_TIMEOUT_MS);
    };

    (async () => {
      armWatchdog();
      try {
        for await (const frame of stream) {
          // Unmounted (or superseded): stop touching state, but keep reading.
          // Breaking would cancel the reader and close the connection, which
          // kills the server's turn; a superseded stream is aborted by its
          // owner and ends here with an AbortError anyway.
          if (cancelled) continue;
          onEventRef.current?.(frame);
          buffer.push(frame);
          schedule();
          armWatchdog();
        }
      } catch (err) {
        if (isAbortError(err)) {
          // The client aborted this stream on purpose (a new turn or Stop),
          // and whoever did owns the turn state now. Finalizing
          // would mark the NEXT turn "connection interrupted", and flushing
          // would pour this turn's frames into it.
          cancelled = true;
        } else if (!cancelled) {
          // A read that throws (connection dropped, proxy reset) used to
          // reject this IIFE unhandled and leave the turn Streaming until the
          // watchdog, 90 s later; a send in the meantime was routed to /steer
          // and queued silently (ELASV1-3514). It is a closed stream:
          // finalize below.
          console.warn("[useStreamingTurn] stream failed", err);
        }
      } finally {
        // Drain anything buffered when the stream ends so the final tokens and
        // the terminal frame (done/complete) aren't stranded waiting for a rAF
        // that the unmount/cancel might cancel.
        if (!cancelled) flush();
        // The stream is closed. If a real terminal frame already arrived this
        // is a no-op; if it closed mid-stream (network drop, backend crash)
        // this finalizes the turn so it never hangs in Streaming.
        if (idleTimer != null) clearTimeout(idleTimer);
        finalizeOnClose();
        if (cancelled && rafId != null) {
          if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(rafId);
          else clearTimeout(rafId as unknown as ReturnType<typeof setTimeout>);
          rafId = null;
        }
      }
    })();

    return () => {
      cancelled = true;
      if (idleTimer != null) clearTimeout(idleTimer);
      if (rafId != null) {
        if (typeof cancelAnimationFrame === "function")
          cancelAnimationFrame(rafId);
        else clearTimeout(rafId as unknown as ReturnType<typeof setTimeout>);
        rafId = null;
      }
    };
  }, [stream]);

  return { ...state, beginTurn, bumpFactsUpdatedAt, finalizeBudgetStop, markCancelled, reset };
}

export function resetTurnState(): TurnState {
  return { ...initialTurnState };
}

export function selectAssistantText(state: TurnState): string {
  return state.assistantParts
    .filter((p): p is AssistantTextPart => p.type === "text")
    .map((p) => p.text)
    .join("");
}
